import { createContext, useContext } from "react";
import type { CallbackOutcome, UserClaims } from "./cognito";

export interface AuthState {
  /** Whether Cognito is configured for this build (else offline/demo mode). */
  readonly configured: boolean;
  readonly authenticated: boolean;
  /** This tab held a session that has since expired (not a first visit). */
  readonly sessionExpired: boolean;
  readonly claims: UserClaims | undefined;
  /**
   * Redirect to the Cognito hosted UI to sign in.
   *
   * `returnTo` is the console path to land on afterwards. It survives the
   * round trip in session storage (the redirect discards React state) and is
   * allow-listed on both ends, so a hostile value degrades to the console root
   * rather than navigating off-origin.
   */
  login: (returnTo?: string) => void;
  /** Clear tokens and redirect to the hosted-UI sign-out. */
  logout: () => void;
  /** Complete a hosted-UI redirect: exchange the code, or report why not. */
  completeLogin: () => Promise<CallbackOutcome>;
  /** The path the interrupted navigation was heading to, if one was stored. */
  consumeReturnPath: () => string;
  /** True once, immediately after a deliberate sign-out. */
  consumeSignedOut: () => boolean;
}

export const AuthCtx = createContext<AuthState | undefined>(undefined);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
